import generatePicker, {
	PickerProps,
	RangePickerProps as RangePickerPropsUi,
} from "antd/lib/date-picker/generatePicker";
import { Dayjs } from "dayjs";
import dayJsGenerateConfig from "rc-picker/lib/generate/dayjs";
import { RangeValue as RangeValueRcPicker } from "rc-picker/lib/interface";
import * as React from "react";

import "./date-picker.scss";

export type DatePickerAbsProps = PickerProps<Dayjs>;

export type RangeValue<DateType> = RangeValueRcPicker<DateType>;

export type DateFilterType = "competence" | "liquidation";

export type RangePickerProps = RangePickerPropsUi<Dayjs> & {
	shouldAdjustRange?: boolean;
	resetInitialValueOnOpen?: boolean;
	showDateTypeSelector?: boolean;
	dateType?: DateFilterType;
	onDateTypeChange?: (type: DateFilterType) => void;
	dateTypeSelectorLabels?: {
		label: string;
		competence: string;
		liquidation: string;
		apply: string;
	};
};

const CustomDatePicker = generatePicker<Dayjs>(dayJsGenerateConfig);

export function DatePicker({ ...props }: DatePickerAbsProps) {
	return <CustomDatePicker {...props} />;
}

function RangePicker({
	value,
	onChange,
	onCalendarChange: onCalendarChangeProp,
	shouldAdjustRange = true,
	resetInitialValueOnOpen = false,
	showDateTypeSelector = false,
	dateType = "competence",
	onDateTypeChange,
	dateTypeSelectorLabels = {
		label: "Filtrar por",
		competence: "Competência",
		liquidation: "Liquidação",
		apply: "Aplicar",
	},
	...props
}: RangePickerProps) {
	const [dates, setDates] = React.useState<[Dayjs | null, Dayjs | null]>(
		value ?? [null, null],
	);
	const [pickerOpen, setPickerOpen] = React.useState(false);
	const [localDateType, setLocalDateType] = React.useState<DateFilterType>(dateType);
	const preventAutoCloseRef = React.useRef(false);

	React.useEffect(() => {
		if (value !== undefined) {
			setDates(value ?? [null, null]);
		}
	}, [value]);

	React.useEffect(() => {
		setLocalDateType(dateType);
	}, [dateType]);

	const renderExtraFooter = React.useCallback(() => {
		if (!showDateTypeSelector) return null;

		const handleApply = () => {
			// Quando showDateTypeSelector está ativo, aplica os valores ao clicar em Aplicar
			// Primeiro atualiza o tipo de data se mudou
			if (onDateTypeChange && localDateType !== dateType) {
				onDateTypeChange(localDateType);
			}

			// Valida se temos um período completo selecionado
			if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
				preventAutoCloseRef.current = false;
				setPickerOpen(false);
				return;
			}

			if (onChange) {
				const asserted = dates as [Dayjs, Dayjs];
				const normalized = shouldAdjustRange
					? (asserted.map((date, i) => {
						date = i === 0 ? date.startOf("day") : date.endOf("day");
						return date;
					}) as [Dayjs, Dayjs])
					: asserted;

				// Chama onChange com os valores normalizados
				onChange(normalized, [
					normalized[0].format("YYYY-MM-DD"),
					normalized[1].format("YYYY-MM-DD"),
				]);
			}

			// Reseta a ref e fecha o calendário ao clicar em Aplicar
			preventAutoCloseRef.current = false;
			setPickerOpen(false);
		};

		return (
			<div className="datePickerTypeSelector">
				<div className="datePickerTypeLeft">
					<span className="datePickerTypeLabel">{dateTypeSelectorLabels.label}</span>
					<div className="datePickerTypeButtons">
						<button
							type="button"
							className={`datePickerTypeButton ${localDateType === "competence" ? "active" : ""
								}`}
							onClick={() => setLocalDateType("competence")}
						>
							{dateTypeSelectorLabels.competence}
						</button>
						<button
							type="button"
							className={`datePickerTypeButton ${localDateType === "liquidation" ? "active" : ""
								}`}
							onClick={() => setLocalDateType("liquidation")}
						>
							{dateTypeSelectorLabels.liquidation}
						</button>
					</div>
				</div>
				<button type="button" className="datePickerTypeApply" onClick={handleApply}>
					{dateTypeSelectorLabels.apply}
				</button>
			</div>
		);
	}, [
		showDateTypeSelector,
		localDateType,
		dateType,
		onDateTypeChange,
		dateTypeSelectorLabels,
		dates,
		onChange,
		shouldAdjustRange,
	]);

	return (
		<CustomDatePicker.RangePicker
			value={dates}
			open={showDateTypeSelector ? pickerOpen : undefined}
			onOpenChange={open => {
				if (showDateTypeSelector) {
					// Previne fechamento automático quando um range completo foi selecionado
					if (!open && preventAutoCloseRef.current) {
						preventAutoCloseRef.current = false;
						return;
					}
					// Reseta a ref quando o calendário é aberto
					if (open) {
						preventAutoCloseRef.current = false;
					}
					// Quando showDateTypeSelector está ativo, controlamos manualmente o estado
					setPickerOpen(open);
				}
				if (open && resetInitialValueOnOpen) {
					return setDates([null, null]);
				}
			}}
			onCalendarChange={(period, formatString, info) => {
				/**
				 * Se showDateTypeSelector está ativo, apenas atualizamos o estado local
				 * sem chamar onChange. A busca só acontecerá ao clicar em "Aplicar"
				 */
				if (showDateTypeSelector) {
					if (!period) {
						setDates([null, null]);
						preventAutoCloseRef.current = false;
						return;
					}

					if (period.length !== 2 || !period.every(Boolean)) {
						// Atualiza o estado mesmo se ainda não tiver ambas as datas selecionadas
						setDates(period);
						preventAutoCloseRef.current = false;
						return;
					}

					// Range completo selecionado - previne fechamento automático
					setDates(period);
					preventAutoCloseRef.current = true;
					// Não fecha o calendário automaticamente nem chama onChange
					return;
				}

				/**
				 * Comportamento padrão quando showDateTypeSelector não está ativo
				 * Use the provided onCalendarChangeProp callback if available,
				 * otherwise fall back to the onChange callback
				 */
				const changeFn = onCalendarChangeProp ?? onChange;

				/**
				 * If no period is provided, reset the dates to null
				 * and call the change function with null values if it exists
				 */
				if (!period) {
					setDates([null, null]);
					if (!changeFn) return;
					changeFn(null, formatString, info);
					return;
				}

				/**
				 * validation and casting is necessary because changeFn expects
				 * a tuple of two values, instead of an array
				 * */
				// eslint-disable-next-line @typescript-eslint/no-magic-numbers
				if (period.length !== 2 || !period.every(Boolean)) return;

				setDates(period);

				if (!changeFn) return;

				const asserted = period as [Dayjs, Dayjs];
				const normalized = shouldAdjustRange
					? (asserted.map((date, i) => {
						date = i === 0 ? date.startOf("day") : date.endOf("day");
						return date;
					}) as [Dayjs, Dayjs])
					: asserted;

				changeFn(normalized, formatString, info);
			}}
			renderExtraFooter={renderExtraFooter}
			{...props}
		/>
	);
}

DatePicker.RangePicker = RangePicker;
DatePicker.TimePicker = CustomDatePicker.TimePicker;
DatePicker.WeekPicker = CustomDatePicker.WeekPicker;
DatePicker.MonthPicker = CustomDatePicker.MonthPicker;
DatePicker.QuarterPicker = CustomDatePicker.QuarterPicker;
DatePicker.YearPicker = CustomDatePicker.YearPicker;
